import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Scales, Warning, CheckCircle, Info } from "@phosphor-icons/react";

const LEVEL_STYLES = {
  ERROR: { bg: "bg-rose-50", border: "border-rose-300", text: "text-rose-900", icon: Warning, iconColor: "text-rose-600" },
  WARN:  { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-900", icon: Warning, iconColor: "text-amber-600" },
  INFO:  { bg: "bg-stone-50", border: "border-stone-200", text: "text-stone-700", icon: Info, iconColor: "text-stone-500" },
  PASS:  { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-900", icon: CheckCircle, iconColor: "text-emerald-600" },
};

export default function LabelingIntelligencePanel({ intel, declaredSoi, onSoiChange, productType }) {
  const [soiOptions, setSoiOptions] = useState([]);

  useEffect(() => {
    if (productType === "wet_sauce" || productType === "dressing") {
      api.get("/labeling/soi-options")
        .then((r) => setSoiOptions(r.data.options || []))
        .catch(() => {});
    }
  }, [productType]);

  if (!intel) return null;

  const findings = intel.findings || [];
  const errorCount = findings.filter((f) => f.level === "ERROR").length;
  const warnCount = findings.filter((f) => f.level === "WARN").length;
  const overallStatus = errorCount > 0 ? "ERROR" : warnCount > 0 ? "WARN" : "PASS";
  const headerStyle = LEVEL_STYLES[overallStatus];

  return (
    <Card className="p-5 border-stone-200" data-testid="labeling-intelligence-panel">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Scales size={18} className="text-brand-700" />
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-stone-500">Labeling Intelligence</div>
            <h3 className="font-semibold mt-0.5">FDA rule scan · 21 CFR 101 / 169</h3>
          </div>
        </div>
        <div className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded ${headerStyle.bg} ${headerStyle.text} border ${headerStyle.border}`}
          data-testid="labeling-status">
          {errorCount > 0 && `${errorCount} error${errorCount > 1 ? "s" : ""}`}
          {errorCount === 0 && warnCount > 0 && `${warnCount} warning${warnCount > 1 ? "s" : ""}`}
          {errorCount === 0 && warnCount === 0 && "All checks passed"}
        </div>
      </div>

      {/* SOI selector — only relevant for wet sauces / dressings */}
      {soiOptions.length > 0 && (
        <div className="mb-4 p-3 rounded-md bg-stone-50 border border-stone-200" data-testid="soi-selector">
          <Label className="text-xs uppercase tracking-wider text-stone-500">Standard of Identity claim</Label>
          <p className="text-[11px] text-stone-500 mt-0.5 mb-2">
            If your label uses a regulated name (Mayonnaise, Salad Dressing, French Dressing),
            select it here to validate the formulation against 21 CFR 169 thresholds.
          </p>
          <Select value={declaredSoi || "none"} onValueChange={(v) => onSoiChange(v === "none" ? null : v)}>
            <SelectTrigger className="bg-white" data-testid="soi-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None — non-standardized dressing</SelectItem>
              {soiOptions.map((o) => (
                <SelectItem key={o.key} value={o.key}>
                  {o.label} ({o.cfr})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {intel.soi_compliance && (
            <div className="mt-2 text-[12px] flex items-center gap-2 flex-wrap">
              <span className="text-stone-500">Oil: <b className={intel.soi_compliance.oil_pct >= intel.soi_compliance.min_oil_pct ? "text-emerald-700" : "text-rose-700"}>{intel.soi_compliance.oil_pct}%</b> / min {intel.soi_compliance.min_oil_pct}%</span>
              <span className="text-stone-300">·</span>
              <span className="text-stone-500">Egg: <b>{intel.soi_compliance.egg_grams}g</b></span>
              <span className="text-stone-300">·</span>
              <span className="text-stone-500">Starch paste: <b>{intel.soi_compliance.starch_paste_present ? "yes" : "no"}</b></span>
              <span className={`ml-auto text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${
                intel.soi_compliance.compliant ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"
              }`}>{intel.soi_compliance.compliant ? "Compliant" : "Non-compliant"}</span>
            </div>
          )}
        </div>
      )}

      {findings.length === 0 ? (
        <div className={`p-3 rounded-md border ${LEVEL_STYLES.PASS.bg} ${LEVEL_STYLES.PASS.border} text-[13px] ${LEVEL_STYLES.PASS.text} flex items-center gap-2`}>
          <CheckCircle size={16} weight="fill" className={LEVEL_STYLES.PASS.iconColor} />
          No labeling issues detected — formula is clean against the rules we check.
        </div>
      ) : (
        <div className="space-y-2" data-testid="labeling-findings">
          {findings.map((f, i) => {
            const s = LEVEL_STYLES[f.level] || LEVEL_STYLES.INFO;
            const Icon = s.icon;
            return (
              <div key={i} className={`p-3 rounded-md border text-[13px] ${s.bg} ${s.border} ${s.text}`}
                data-testid={`finding-${f.code}`}>
                <div className="flex items-start gap-2">
                  <Icon size={16} weight="fill" className={`${s.iconColor} flex-shrink-0 mt-0.5`} />
                  <div className="flex-1">
                    <div className="font-semibold flex items-center gap-2 flex-wrap">
                      <span>{f.title}</span>
                      <span className="text-[9px] uppercase tracking-wider font-bold opacity-60 bg-white/60 px-1.5 py-0.5 rounded">
                        {f.cfr}
                      </span>
                    </div>
                    <div className="mt-1 leading-relaxed">{f.message}</div>
                    {f.auto_fix && (
                      <div className="mt-1.5 text-[11px]">
                        <span className="text-stone-500">Suggested label text: </span>
                        <code className="font-mono bg-white/70 px-1.5 py-0.5 rounded">{f.auto_fix}</code>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {intel.spice_aggregation?.eligible?.length >= 4 && (
        <div className="mt-3 text-[11px] text-stone-500 leading-relaxed bg-stone-50 border border-stone-200 rounded px-3 py-2">
          <b>Tip:</b> {intel.spice_aggregation.eligible.length} ingredients qualify to be aggregated as
          a single &quot;Spices&quot; entry on your label (21 CFR 101.22(h)(1)) — keeps the panel clean and
          protects proprietary blend ratios.
        </div>
      )}
    </Card>
  );
}
